import { useClerk, useUser } from '@clerk/clerk-react'
import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import type { RouteObject } from 'react-router-dom'

import Spinner from '@components/atoms/spinner'
import { Column } from '@components/templates/column'
import { platform } from '@utils/capacitor-helper'
import { getRequiredOnboardingPath } from '@utils/user-onboarding'

function resolveCallbackUrl(path: string) {
    if (typeof window === 'undefined') return path

    // On native, window.location.origin is capacitor://localhost which Clerk
    // cannot use as a redirect URL. Use the HTTPS universal-link origin instead.
    if (platform !== 'web') {
        const nativeOrigin = import.meta.env.DEV
            ? 'https://dev.group.splitcard.com'
            : 'https://group.splitcard.com'
        return `${nativeOrigin}${path}`
    }

    return new URL(path, window.location.origin).toString()
}

function normalizeRedirectTarget(target: string | null): string | null {
    if (!target) return null

    try {
        const url = new URL(target, window.location.origin)
        return `${url.pathname}${url.search}${url.hash}`
    } catch {
        return target.startsWith('/') ? target : null
    }
}

function isBareSignInTarget(target: string | null): boolean {
    if (!target?.startsWith('/sign-in')) return false
    return !target.includes('?')
}

function isBareSignUpTarget(target: string | null): boolean {
    return target === '/sign-up' || target === '/sign-up/'
}

function hasClerkRedirectParams(searchParams: URLSearchParams): boolean {
    return [
        '__clerk_status',
        '__clerk_created_session',
        '__clerk_ticket',
        'code',
        'state',
        'rotating_token_nonce',
    ].some((param) => searchParams.has(param))
}

// Polls Clerk's client state until a session or sign-up requirement is detected.
// This is primarily used for web OAuth redirect flows. On native, the sign-in
// component's own polling loop handles session detection instead.
async function waitForClerkAuthState(clerk: ReturnType<typeof useClerk>, timeoutMs = 8000, intervalMs = 250) {
    const start = Date.now()

    while (Date.now() - start < timeoutMs) {
        if (clerk.user || clerk.session || clerk.client?.signUp?.status === 'missing_requirements') {
            return true
        }

        await clerk.client?.reload()
        await new Promise(resolve => window.setTimeout(resolve, intervalMs))
    }

    return false
}

const AuthSsoCallbackScreen = () => {
    const clerk = useClerk()
    const { isLoaded, isSignedIn, user } = useUser()
    const navigate = useNavigate()
    const [searchParams] = useSearchParams()
    const [callbackHandled, setCallbackHandled] = useState(false)
    const [pendingRedirect, setPendingRedirect] = useState<string | null>(null)
    const [callbackError, setCallbackError] = useState<string | null>(null)
    const shouldHandleClerkCallback = platform === 'web' || hasClerkRedirectParams(searchParams)
    const fallbackRedirect = normalizeRedirectTarget(searchParams.get('returnTo'))

    // Native sign-in can complete server-side without callback params reaching
    // the app. But connected-account linking still needs Clerk callback
    // processing when those params do reach /sso-callback.
    useEffect(() => {
        if (!shouldHandleClerkCallback) {
const returnTo = searchParams.get('returnTo')
            if (returnTo?.startsWith('/')) {
                navigate(returnTo, { replace: true })
            } else {
                // Preserve referralId so the auth layout can claim the promo
                const referralId = searchParams.get('referralId')
                const query = referralId ? `?referralId=${encodeURIComponent(referralId)}` : ''
                navigate(`/sign-in${query}`, { replace: true, state: { initialView: 'form' } })
            }
        }
    }, [navigate, searchParams, shouldHandleClerkCallback])

    useEffect(() => {
        if (!shouldHandleClerkCallback) return
        if (!clerk.loaded || callbackHandled || callbackError) return

        let cancelled = false

        async function handleCallback() {
            const signInUrl = resolveCallbackUrl('/sign-in')
            const continueSignUpUrl = resolveCallbackUrl('/sign-up/add-info')

            try {
                await clerk.handleRedirectCallback({
                    transferable: true,
                    signInUrl,
                    signUpUrl: signInUrl,
                    continueSignUpUrl,
                    signInFallbackRedirectUrl: signInUrl,
                    signUpFallbackRedirectUrl: signInUrl,
                }, async (to) => {
                    if (!cancelled) setPendingRedirect(normalizeRedirectTarget(typeof to === 'string' ? to : null))
                })

                const authSettled = await waitForClerkAuthState(clerk)

                if (!cancelled) {
                    if (!authSettled) {
                        setCallbackError('Unable to finish signing you in.')
                        return
                    }

                    setCallbackHandled(true)
                }
            } catch (error: unknown) {
                console.error('[ Auth SSO Callback ]', error)
                if (!cancelled) setCallbackError('Unable to finish signing you in.')
            }
        }

        void handleCallback()

        return () => {
            cancelled = true
        }
    }, [callbackError, callbackHandled, clerk, shouldHandleClerkCallback])

    useEffect(() => {
        if (!callbackHandled || !isLoaded) return

        const signUp = clerk.client?.signUp
        if (signUp?.status === 'missing_requirements') {
            navigate('/sign-up/add-info', { replace: true })
            return
        }

        if (isSignedIn && user) {
            const onboardingPath = getRequiredOnboardingPath(user)
            const resolvedRedirect = pendingRedirect ?? fallbackRedirect
            const redirectTarget = resolvedRedirect
                && !isBareSignInTarget(resolvedRedirect)
                && !isBareSignUpTarget(resolvedRedirect)
                ? resolvedRedirect
                : onboardingPath ?? '/account'

            navigate(redirectTarget, { replace: true })
            return
        }

        const resolvedRedirect = pendingRedirect ?? fallbackRedirect
        if (resolvedRedirect && !isBareSignInTarget(resolvedRedirect) && !isBareSignUpTarget(resolvedRedirect)) {
            navigate(resolvedRedirect, { replace: true })
            return
        }

        navigate('/account', { replace: true })
    }, [callbackHandled, clerk.client, fallbackRedirect, isLoaded, isSignedIn, navigate, pendingRedirect, user])

    useEffect(() => {
        if (!callbackError) return
        navigate('/sign-in', {
            replace: true,
            state: {
                initialView: 'form',
                intent: 'sign-in',
            },
        })
    }, [callbackError, navigate])

    return (
        <>
            <Column.Content className='auth-sign-in-screen'>
                <div className='flex flex-col items-center justify-center gap-4 w-full h-full'>
                    <Spinner indicator='circle' role='status' aria-label='Completing sign in' />
                    <p>{callbackError || 'Completing sign in...'}</p>
                </div>
            </Column.Content>
            <Column.Footer hidden />
        </>
    )
}

const SsoCallbackRoute: RouteObject = {
    path: 'sso-callback',
    element: <AuthSsoCallbackScreen />,
}

export default SsoCallbackRoute
